import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
  InjectionToken,
  Inject
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Meta, Title } from '@angular/platform-browser';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import { AppConfigSettings, ArcValidators } from '@arc/arc-shared-frontend';
import { InputComponent } from '@arc/aureus';
import { ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-authentication-login',
  styleUrls: ['./login.component.scss'],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit, AfterViewInit {
  @Output()
  signinClicked = new EventEmitter<any>();
  @Output()
  forgotPasswordClicked = new EventEmitter<any>();
  @Input()
  showLoginButton = true;
  @Input()
  showForgetPasswordButton = true;
  @Input()
  msgs = [];
  @Input()
  submitting;

  @ViewChild('email') emailInputComponentRef: InputComponent;
  @ViewChild('password') passwordInputComponentRef: InputComponent;

  get privacyPolicy(): string {
    switch (this.route.snapshot.parent.url[0].path?.toLowerCase()) {
      case 'jpmorgan':
        return this.settings.getconfig('jpmorganPrivacyPolicyUrl');
      default:
        return this.settings.getconfig('billtrustPrivacyPolicyUrl');
    }
  };

  get jpmorganEConsent(): boolean {
    switch (this.route.snapshot.parent.url[0].path?.toLowerCase()) {
      case 'jpmorgan':
        return true;
      default:
        return false;
    }
  };

  private get resellerId(): string {
    return this.route.snapshot.parent.url[0].path
  }

  get emailControl() {
    return this.loginFormGroup.get('email');
  }
  get passwordControl() {
    return this.loginFormGroup.get('password');
  }

  get emailIsInValid() {
    return this.emailControl.errors && this.emailControl.touched;
  }
  get passwordIsInValid() {
    return this.passwordControl.errors && this.passwordControl.touched;
  }

  econsentDialogVisible: boolean;

  loginFormGroup: UntypedFormGroup;
  constructor(
    private changeDetector: ChangeDetectorRef,
    private route: ActivatedRoute,
    private settings: AppConfigSettings, 
    private titleService:Title,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    this.loginFormGroup = new UntypedFormGroup({
      email: new UntypedFormControl('', {
        validators: [Validators.required, ArcValidators.email],
        updateOn: 'submit'
      }),
      password: new UntypedFormControl('', {
        validators: [Validators.required],
        updateOn: 'submit'
      })
    });

    this.changeTabHeader(this.route.snapshot.parent.url[0].path);
  }

  showEConsentDialog(): void {
    this.econsentDialogVisible = true;
  }

  changeTabHeader(reseller: string) {
    const link: HTMLLinkElement = this.document?.querySelector("link[rel='icon']");
    if (link) {
      switch (reseller?.toLowerCase()) {
        case 'jpmorgan':
          link.href = 'assets/layout/images/jpm-favicon.ico?v1';
          this.titleService.setTitle(this.settings.getconfig('jpmorganTabTitle'));
          break;
        default:
          link.href = 'assets/layout/images/favicon.ico?v1';
          this.titleService.setTitle(this.settings.getconfig('billtrustTabTitle'));
          break;   
      }
    }
  }

  ngAfterViewInit(): void {
    const emailElement = this.emailInputComponentRef.nativeInput.nativeElement;
    const passwordElement =
      this.passwordInputComponentRef.nativeInput.nativeElement;
    this.defineValueProperty(emailElement);
    this.defineValueProperty(passwordElement);

    this.emailInputComponentRef.nativeInput.nativeElement.addEventListener(
      'valuechange',
      e => {
        this.emailControl.setValue(e.detail.data);
        this.emailControl.updateValueAndValidity();
        this.changeDetector.detectChanges();
      }
    );

    this.passwordInputComponentRef.nativeInput.nativeElement.addEventListener(
      'valuechange',
      e => {
        this.passwordControl.setValue(e.detail.data);
        this.passwordControl.updateValueAndValidity();
        this.changeDetector.detectChanges();
      }
    );

    this.emailInputComponentRef.nativeInput.nativeElement.focus();
  }

  onSignin() {
    if (this.loginFormGroup.valid) {
      this.signinClicked.emit(this.loginFormGroup.value);
    } else {
      this.emailControl.markAsTouched();
      this.passwordControl.markAsTouched();
    }
  }

  onForgotPassword() {
    this.forgotPasswordClicked.emit(this.emailControl.value);
  }

  onEmailEnterKey(value: string | number) {
    this.emailControl.setValue(value);
  }

  onPasswordEnterKey(value: string | number) {
    this.passwordControl.setValue(value);
  }

  defineValueProperty(element: any) {
    var descriptor = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      'value'
    );
    var originalSet = descriptor.set;
    descriptor.set = function (val) {
      if (this.value !== val) {
        originalSet.apply(this, arguments);
        var a: CustomEventInit;
        var event = new CustomEvent<any>('valuechange', {
          detail: { data: val }
        });
        this.dispatchEvent(event);
      } else {
        originalSet.apply(this, arguments);
      }
    };
    Object.defineProperty(element, 'value', descriptor);
  }

  emailValueChanged($event) {
    this.emailControl.setValue($event);
    this.emailControl.updateValueAndValidity();
  }

  passwordValueChanged($event) {
    this.passwordControl.setValue($event);
    this.passwordControl.updateValueAndValidity();
  }
}

